import React, { useState, useEffect, useMemo } from 'react';
import { personaleApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, LineChart as LineChartIcon } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { fmtSoldi, fmtPct, fmtQta, ND } from '../../lib/acquisti';
import { GRANULARITA, aggrega, conAnnoPrima, div, pctSu } from '../../lib/trend';

const COLORE = { buste: '#D94F3D', contributi: '#A8705F', cassa: '#D97706', linea: '#1C1917', prec: '#A8A29E' };

const METRICHE = [
  { key: 'composizione', label: 'Composizione uscite (buste, contributi, cassa)', tipo: 'stack' },
  { key: 'costo_totale', label: 'Costo totale del personale', tipo: 'euro' },
  { key: 'buste_paga', label: 'Buste paga', tipo: 'euro' },
  { key: 'contributi', label: 'Contributi (INPS, INAIL, TFR)', tipo: 'euro' },
  { key: 'uscite_cassa', label: 'Uscite di cassa (personale extra)', tipo: 'euro' },
  { key: 'fatturato', label: 'Fatturato (venduto)', tipo: 'euro' },
  { key: 'incidenza_totale_pct', label: 'Incidenza sul fatturato', tipo: 'pct' },
  { key: 'costo_ora', label: 'Costo per ora', tipo: 'unitario' },
  { key: 'ore', label: 'Ore lavorate', tipo: 'num' },
  { key: 'costo_per_coperto', label: 'Costo per coperto', tipo: 'unitario' },
  { key: 'ricavo_per_ora', label: 'Ricavo per ora', tipo: 'unitario' },
];

const CAMPI_SOMMA = [
  'buste_paga', 'contributi', 'uscite_cassa', 'costo_totale', 'ore',
  'fatturato', 'coperti', 'giornate', 'n_uscite_cassa',
];

const deriva = (g) => ({
  incidenza_totale_pct: pctSu(g.costo_totale, g.fatturato),
  incidenza_buste_pct: pctSu(g.buste_paga, g.fatturato),
  incidenza_contributi_pct: pctSu(g.contributi, g.fatturato),
  incidenza_cassa_pct: pctSu(g.uscite_cassa, g.fatturato),
  costo_ora: div(g.costo_totale, g.ore),
  costo_per_coperto: div(g.costo_totale, g.coperti),
  ricavo_per_ora: div(g.fatturato, g.ore),
  cedolini: g.righe.filter((r) => r.cedolino_presente).length,
});

const fmtAsse = (v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`);

const Chip = ({ attivo, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
      attivo ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
    }`}
  >
    {children}
  </button>
);

const TooltipTrend = ({ active, payload, metrica, percentuale }) => {
  if (!active || !payload?.length) return null;
  const g = payload[0].payload;
  const soldi = (v) => (percentuale ? fmtPct(pctSu(v, g.fatturato), 2) : fmtSoldi(v));
  return (
    <div className="rounded-lg border bg-background/95 p-2.5 text-xs shadow-lg space-y-1 max-w-[230px]">
      <p className="font-bold capitalize">{g.label}</p>
      {metrica.tipo === 'stack' ? (
        <>
          <p className="flex justify-between gap-3"><span className="text-muted-foreground">Buste paga</span><span className="font-semibold tabular-nums">{soldi(g.buste_paga)}</span></p>
          <p className="flex justify-between gap-3"><span className="text-muted-foreground">Contributi</span><span className="font-semibold tabular-nums">{soldi(g.contributi)}</span></p>
          <p className="flex justify-between gap-3"><span className="text-muted-foreground">Uscite di cassa</span><span className="font-semibold tabular-nums">{soldi(g.uscite_cassa)}</span></p>
          <p className="flex justify-between gap-3 border-t pt-1"><span className="text-muted-foreground">Totale</span><span className="font-bold tabular-nums">{soldi(g.costo_totale)}</span></p>
        </>
      ) : (
        <>
          <p className="flex justify-between gap-3">
            <span className="text-muted-foreground">{metrica.label}</span>
            <span className="font-bold tabular-nums">
              {metrica.tipo === 'pct' ? fmtPct(g.valore, 2)
                : metrica.tipo === 'num' ? fmtQta(g.valore)
                  : percentuale && metrica.tipo === 'euro' ? fmtPct(pctSu(g.valore, g.fatturato), 2)
                    : fmtSoldi(g.valore)}
            </span>
          </p>
          {g.valore_prec !== null && g.valore_prec !== undefined && (
            <p className="text-[11px] text-muted-foreground">
              {g.label_prec}: {metrica.tipo === 'num' ? fmtQta(g.valore_prec) : metrica.tipo === 'pct' ? fmtPct(g.valore_prec, 2) : fmtSoldi(g.valore_prec)}
              {g.delta_pct !== null && ` · ${g.delta_pct > 0 ? '+' : ''}${String(g.delta_pct).replace('.', ',')}%`}
            </p>
          )}
        </>
      )}
      <p className="text-[11px] text-muted-foreground border-t pt-1">
        fatturato {fmtSoldi(g.fatturato)} · {fmtQta(g.ore)} ore
        {g.cedolini < g.n_mesi ? ` · ${g.n_mesi - g.cedolini} mesi senza cedolino` : ''}
      </p>
    </div>
  );
};

/** Trend del costo del personale: metrica, granularità, confronto anno prima, € o % sul fatturato. */
export const TrendPersonaleCard = ({ className = '' }) => {
  const [serie, setSerie] = useState(null);
  const [caricando, setCaricando] = useState(true);
  const [metricaKey, setMetricaKey] = useState('composizione');
  const [gran, setGran] = useState('mese');
  const [percentuale, setPercentuale] = useState(false);
  const [annoPrima, setAnnoPrima] = useState(false);

  useEffect(() => {
    personaleApi.serie()
      .then(setSerie)
      .catch((e) => toast.error(e.response?.data?.detail || 'Trend del personale non disponibile'))
      .finally(() => setCaricando(false));
  }, []);

  const metrica = METRICHE.find((m) => m.key === metricaKey) || METRICHE[0];
  const pctApplicabile = metrica.tipo === 'stack' || metrica.tipo === 'euro';
  const inPct = percentuale && pctApplicabile;

  const gruppi = useMemo(() => {
    const base = aggrega(serie?.mesi || [], gran, CAMPI_SOMMA, deriva);
    return conAnnoPrima(base, gran, metrica.key === 'composizione' ? 'costo_totale' : metrica.key);
  }, [serie, gran, metrica.key]);

  const dati = useMemo(() => gruppi.map((g) => ({
    ...g,
    b: inPct ? g.incidenza_buste_pct : g.buste_paga,
    c: inPct ? g.incidenza_contributi_pct : g.contributi,
    u: inPct ? g.incidenza_cassa_pct : g.uscite_cassa,
    v: inPct && metrica.tipo === 'euro' ? pctSu(g.valore, g.fatturato) : g.valore,
    vp: annoPrima
      ? (inPct && metrica.tipo === 'euro' ? null : g.valore_prec)
      : null,
  })), [gruppi, inPct, metrica.tipo, annoPrima]);

  if (caricando) {
    return (
      <Card className={className} data-testid="trend-personale-caricamento">
        <CardContent className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Calcolo dei trend del personale…
        </CardContent>
      </Card>
    );
  }
  if (!serie?.mesi?.length) return null;

  const ultimo = gruppi[gruppi.length - 1];
  const totale = gruppi.reduce((s, g) => s + (g.costo_totale || 0), 0);
  const fatturatoTot = gruppi.reduce((s, g) => s + (g.fatturato || 0), 0);

  return (
    <Card className={className} data-testid="trend-personale">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <LineChartIcon className="w-4 h-4 text-primary" /> Trend del personale
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={metricaKey} onValueChange={setMetricaKey}>
            <SelectTrigger className="h-9 text-xs w-full sm:w-[320px]" data-testid="trend-personale-metrica">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {METRICHE.map((m) => (
                <SelectItem key={m.key} value={m.key} className="text-xs" data-testid={`trend-personale-metrica-${m.key}`}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-1.5" data-testid="trend-personale-granularita">
            {GRANULARITA.map((g) => (
              <Chip key={g.key} attivo={gran === g.key} onClick={() => setGran(g.key)} testid={`trend-personale-gran-${g.key}`}>
                {g.label}
              </Chip>
            ))}
          </div>
          <div className="flex gap-1.5 ml-auto">
            <Chip
              attivo={inPct}
              onClick={() => setPercentuale(!percentuale)}
              testid="trend-personale-percentuale"
            >
              {pctApplicabile ? (inPct ? '% sul fatturato' : '€') : 'valore'}
            </Chip>
            <Chip
              attivo={annoPrima}
              onClick={() => setAnnoPrima(!annoPrima)}
              testid="trend-personale-anno-prima"
            >
              vs anno prima
            </Chip>
          </div>
        </div>

        <div className="h-64 lg:h-80 -ml-2" data-testid="trend-personale-grafico">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={dati} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E7E5E4" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis
                yAxisId="val"
                tick={{ fontSize: 10 }}
                width={44}
                tickFormatter={(v) => (inPct || metrica.tipo === 'pct' ? `${Math.round(v)}%` : fmtAsse(v))}
              />
              {metrica.tipo === 'stack' && !inPct && (
                <YAxis
                  yAxisId="pct"
                  orientation="right"
                  tick={{ fontSize: 10 }}
                  width={38}
                  tickFormatter={(v) => `${Math.round(v)}%`}
                />
              )}
              <Tooltip content={<TooltipTrend metrica={metrica} percentuale={inPct} />} />
              {metrica.tipo === 'stack' ? (
                <>
                  <Bar yAxisId="val" stackId="p" dataKey="b" name="Buste paga" fill={COLORE.buste} />
                  <Bar yAxisId="val" stackId="p" dataKey="c" name="Contributi" fill={COLORE.contributi} />
                  <Bar yAxisId="val" stackId="p" dataKey="u" name="Uscite di cassa" fill={COLORE.cassa} radius={[3, 3, 0, 0]} />
                  {!inPct && (
                    <Line
                      yAxisId="pct"
                      type="monotone"
                      dataKey="incidenza_totale_pct"
                      name="Incidenza %"
                      stroke={COLORE.linea}
                      strokeWidth={2}
                      dot={{ r: 2.5 }}
                      connectNulls={false}
                    />
                  )}
                </>
              ) : (
                <>
                  {annoPrima && (
                    <Bar yAxisId="val" dataKey="vp" name="Anno prima" fill={COLORE.prec} radius={[3, 3, 0, 0]} />
                  )}
                  <Bar yAxisId="val" dataKey="v" name={metrica.label} fill={COLORE.buste} radius={[3, 3, 0, 0]} />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
          {metrica.tipo === 'stack' && (
            <>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORE.buste }} /> Buste paga</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORE.contributi }} /> Contributi</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORE.cassa }} /> Uscite di cassa</span>
              {!inPct && <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORE.linea }} /> Incidenza % sul fatturato</span>}
            </>
          )}
          {serie.n_mesi_con_cedolino < serie.n_mesi && (
            <span className="text-amber-700">
              {serie.n_mesi - serie.n_mesi_con_cedolino} mesi hanno solo uscite di cassa, senza cedolino
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t sm:grid-cols-4" data-testid="trend-personale-riepilogo">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Uscite personale</p>
            <p className="font-bold tabular-nums">{fmtSoldi(totale)}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Incidenza media</p>
            <p className="font-bold tabular-nums">{fmtPct(pctSu(totale, fatturatoTot), 2)}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Ultimo periodo</p>
            <p className="font-bold tabular-nums capitalize">{ultimo?.label || ND}</p>
            <p className="text-[11px] text-muted-foreground">{fmtSoldi(ultimo?.costo_totale)}</p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">vs anno prima</p>
            <p className="font-bold tabular-nums">
              {ultimo?.delta_pct === null || ultimo?.delta_pct === undefined
                ? ND
                : `${ultimo.delta_pct > 0 ? '+' : ''}${String(ultimo.delta_pct).replace('.', ',')}%`}
            </p>
            <p className="text-[11px] text-muted-foreground">{ultimo?.label_prec || 'nessun confronto'}</p>
          </div>
        </div>

        <div className="overflow-x-auto -mx-4 px-4" data-testid="trend-personale-tabella">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b">
                <th className="text-left font-medium py-1.5">Periodo</th>
                <th className="text-right font-medium py-1.5">Buste paga</th>
                <th className="text-right font-medium py-1.5">Contributi</th>
                <th className="text-right font-medium py-1.5">Uscite cassa</th>
                <th className="text-right font-medium py-1.5">Totale</th>
                <th className="text-right font-medium py-1.5">% fatturato</th>
                <th className="text-right font-medium py-1.5">Ore</th>
                <th className="text-right font-medium py-1.5">€/ora</th>
              </tr>
            </thead>
            <tbody>
              {[...gruppi].reverse().map((g) => (
                <tr key={g.chiave} className="border-b last:border-0" data-testid={`trend-personale-riga-${g.chiave}`}>
                  <td className="py-1.5 capitalize whitespace-nowrap">{g.label}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtSoldi(g.buste_paga)}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtSoldi(g.contributi)}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtSoldi(g.uscite_cassa)}</td>
                  <td className="py-1.5 text-right tabular-nums font-semibold">{fmtSoldi(g.costo_totale)}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtPct(g.incidenza_totale_pct, 2)}</td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">{fmtQta(g.ore)}</td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">{g.costo_ora ? fmtSoldi(g.costo_ora) : ND}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrendPersonaleCard;
